import { useRef, useEffect, useState } from 'react'

export function useInterval(callback: Function, delay: number) {
  const savedCallback = useRef<Function | null>(null)

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (savedCallback && savedCallback.current) {
        savedCallback.current()
      }
    }
    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

/**
 * Create a value that updates every `delay` milliseconds.
 * @param callback The callback to run every `delay` milliseconds, returning the value.
 * @param delay How often to run the callback.
 * @param runOnceOnTrue Run the callback once when this changes to true.
 */
export function useIntervalValue<T>(
  callback: (prev: T | null) => T,
  delay: number,
  runOnceOnTrue?: boolean
) {
  if (delay <= 0) throw new Error('Delay must be greater than 0')

  const savedCallback = useRef<(prev: T | null) => T>(callback)
  const hasRunOnceOnTrue = useRef(false)

  const [value, setValue] = useState<T>(callback(null))

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    function tick() {
      setValue(v => savedCallback.current(v))
    }

    const id = setInterval(tick, delay)
    return () => clearInterval(id)
  }, [delay])

  useEffect(() => {
    if (!runOnceOnTrue || hasRunOnceOnTrue.current) return

    setValue(v => savedCallback.current(v))
    hasRunOnceOnTrue.current = true
  }, [runOnceOnTrue])

  return value
}
